import { Button } from '@/components/ui/button'
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { DEFAULT_RADIUS_VALUE, RADIUS_OPTIONS } from 'honeygrid-types'
import { useEffect, useState } from 'react'

import type { County, WorkspaceLocation, TargetingType } from 'honeygrid-types'
import type { FC } from 'react'

interface AddTargetedAreaModalProps {
	open: boolean
	onClose: () => void
	onSave: (targetedArea: {
		type: TargetingType
		data: {
			locationId?: WorkspaceLocation['uuid']
			radius?: number
			counties?: string[]
			zipCodes?: string[]
		}
	}) => void
	onRemove?: () => void
	locations?: WorkspaceLocation[]
	counties: County[]
	zipCodes: string[]
	initialData?: {
		type: TargetingType
		data: {
			locationId?: WorkspaceLocation['uuid']
			radius?: number
			county?: County['id']
			zipCode?: string
		}
	}
}

export const AddTargetedAreaModal: FC<AddTargetedAreaModalProps> = ({
	open,
	onClose,
	onSave,
	onRemove,
	locations,
	counties,
	zipCodes,
	initialData,
}) => {
	const [targetingType, setTargetingType] = useState<TargetingType>('radius')
	const [selectedLocation, setSelectedLocation] = useState<WorkspaceLocation['uuid'] | undefined>(undefined)
	const [radius, setRadius] = useState<number>(DEFAULT_RADIUS_VALUE)
	const [selectedCounties, setSelectedCounties] = useState<string[]>([])
	const [selectedZipCodes, setSelectedZipCodes] = useState<string[]>([])
	const [bulkInput, setBulkInput] = useState<string>('')
	const [isBulkMode, setIsBulkMode] = useState(false)

	const resetState = () => {
		setTargetingType('radius')
		setSelectedLocation(locations?.[0]?.uuid ?? undefined)
		setRadius(DEFAULT_RADIUS_VALUE)
		setSelectedCounties([])
		setSelectedZipCodes([])
		setBulkInput('')
		setIsBulkMode(false)
	}

	useEffect(() => {
		if (open) {
			if (initialData) {
				setTargetingType(initialData.type)
				setSelectedLocation(initialData.data.locationId ?? undefined)
				setRadius(initialData.data.radius ?? DEFAULT_RADIUS_VALUE)
				setSelectedCounties(initialData.data.county ? [initialData.data.county] : [])
				setSelectedZipCodes(initialData.data.zipCode ? [initialData.data.zipCode] : [])
				setBulkInput('')
				setIsBulkMode(false)
			} else {
				resetState()
			}
		}
	}, [open, initialData, locations])

	const handleClose = () => {
		onClose()
		resetState()
	}

	const handleSave = () => {
		switch (targetingType) {
			case 'radius': {
				if (!selectedLocation) return
				onSave({
					type: targetingType,
					data: {
						locationId: selectedLocation,
						radius,
					},
				})
				break
			}

			case 'county': {
				let countyIds: string[] = []

				if (isBulkMode && bulkInput) {
					// Process bulk input
					const lines = bulkInput
						.split('\n')
						.map((line) => line.trim())
						.filter(Boolean)

					countyIds = lines
						.map((line) => {
							const [countyName, state] = line.split(',').map((s) => s.trim())
							const normalizedCountyName = countyName.toLowerCase().replace(/\s+county$/, '')
							const normalizedState = state.toLowerCase()

							const county = counties.find((c) => {
								const countyNameNormalized = c.name.toLowerCase().replace(/\s+county$/, '')
								return (
									countyNameNormalized === normalizedCountyName &&
									c.state.toLowerCase() === normalizedState
								)
							})

							if (!county) {
								console.warn(`County not found: ${countyName}, ${state}`)
								return null
							}
							return county.id
						})
						.filter((id): id is string => id !== null)
				} else {
					countyIds = selectedCounties
				}

				if (countyIds.length === 0) return

				onSave({
					type: targetingType,
					data: {
						counties: countyIds,
					},
				})
				break
			}

			case 'zip code': {
				let zipCodeList: string[] = []

				if (isBulkMode && bulkInput) {
					zipCodeList = bulkInput
						.split('\n')
						.map((line) => line.trim())
						.filter(Boolean)
				} else {
					zipCodeList = selectedZipCodes
				}

				if (zipCodeList.length === 0) return

				onSave({
					type: targetingType,
					data: {
						zipCodes: zipCodeList,
					},
				})
				break
			}
		}

		handleClose()
	}

	return (
		<Dialog open={open} onOpenChange={(open) => !open && handleClose()}>
			<DialogContent className="sm:max-w-[425px]">
				<DialogHeader>
					<DialogTitle>{initialData ? 'Edit' : 'Add'} Targeted Area</DialogTitle>
				</DialogHeader>
				<div className="grid gap-4 py-4">
					<div className="grid gap-2">
						<Label htmlFor="targeting-type">Targeting Type</Label>
						<Select
							disabled={!!initialData}
							value={targetingType}
							onValueChange={(value: TargetingType) => setTargetingType(value)}
						>
							<SelectTrigger id="targeting-type">
								<SelectValue placeholder="Select targeting type" />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="radius">Radius around location</SelectItem>
								<SelectItem value="county">Counties</SelectItem>
								<SelectItem value="zipcode">Zip codes</SelectItem>
							</SelectContent>
						</Select>
					</div>

					{targetingType === 'radius' && (
						<>
							<div className="grid gap-2">
								<Label htmlFor="location">Location</Label>
								<Select
									value={selectedLocation ?? ''}
									onValueChange={(value) => setSelectedLocation(value)}
								>
									<SelectTrigger id="location">
										<SelectValue placeholder="Select location" />
									</SelectTrigger>
									<SelectContent>
										{locations?.map((location) => (
											<SelectItem key={location.uuid} value={location.uuid}>
												{location.name}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
							<div className="grid gap-2">
								<Label htmlFor="radius">Radius</Label>
								<Select
									value={radius.toString()}
									onValueChange={(value) => setRadius(Number(value))}
								>
									<SelectTrigger id="radius">
										<SelectValue placeholder="Select radius" />
									</SelectTrigger>
									<SelectContent>
										{RADIUS_OPTIONS.map((option) => (
											<SelectItem key={option} value={option.toString()}>
												{`${option} miles${option === DEFAULT_RADIUS_VALUE ? ' (default)' : ''}`}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
						</>
					)}

					{(targetingType === 'county' || targetingType === 'zip code') && (
						<>
							<Button variant="outline" onClick={() => setIsBulkMode(!isBulkMode)}>
								{isBulkMode
									? 'Switch to dropdown selection'
									: `Bulk add ${targetingType === 'county' ? 'counties' : 'zip codes'}`}
							</Button>

							{isBulkMode ? (
								<div className="grid gap-2">
									<Label htmlFor="bulk-input">
										{targetingType === 'county'
											? 'Enter counties (one per line)'
											: 'Enter zip codes (one per line)'}
									</Label>
									<Textarea
										id="bulk-input"
										value={bulkInput}
										onChange={(e) => setBulkInput(e.target.value)}
										placeholder={
											targetingType === 'county' ? 'Format: County, State' : 'Enter zip codes'
										}
										className="h-[120px]"
									/>
								</div>
							) : (
								<div className="grid gap-2">
									<Label htmlFor="selection">
										{targetingType === 'county' ? 'Counties' : 'Zip Codes'}
									</Label>
									<Select
										value={
											targetingType === 'county'
												? selectedCounties[0] || ''
												: selectedZipCodes[0] || ''
										}
										onValueChange={(value) => {
											if (targetingType === 'county') {
												setSelectedCounties([value])
											} else {
												setSelectedZipCodes([value])
											}
										}}
									>
										<SelectTrigger id="selection">
											<SelectValue
												placeholder={`Select ${targetingType === 'county' ? 'county' : 'zip code'}`}
											/>
										</SelectTrigger>
										<SelectContent>
											{targetingType === 'county'
												? counties.map((county) => (
														<SelectItem key={county.id} value={county.id}>
															{county.name}, {county.state}
														</SelectItem>
													))
												: zipCodes.map((zipCode) => (
														<SelectItem key={zipCode} value={zipCode}>
															{zipCode}
														</SelectItem>
													))}
										</SelectContent>
									</Select>
								</div>
							)}
						</>
					)}
				</div>
				<DialogFooter className="flex justify-end gap-2">
					{initialData && onRemove && (
						<Button
							variant="destructive"
							onClick={() => {
								onRemove()
								handleClose()
							}}
						>
							Remove
						</Button>
					)}
					<Button variant="secondary" onClick={handleClose}>
						Cancel
					</Button>
					<Button onClick={handleSave}>{initialData ? 'Save Changes' : 'Add'}</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
